(() => {
  const ROOT_SELECTOR =
    '[data-interactive-collage-init]';

  const LIST_SELECTOR =
    '[data-interactive-collage-list]';

  const ITEM_SELECTOR =
    '[data-interactive-collage-item]';

  const INNER_SELECTOR =
    '[data-interactive-collage-item-inner]';

  const CONTROL_SELECTOR =
    '[data-interactive-collage-control]';

  const FINE_POINTER_QUERY =
    '(hover: hover) and (pointer: fine)';

  const REDUCED_MOTION_QUERY =
    '(prefers-reduced-motion: reduce)';

  const ACTIVE_SCALE = 1.075;
  const INACTIVE_SCALE = 0.9;
  const GAP_PERCENT = 3;
  const SECOND_CARD_BOOST = 1.35;
  const CENTER_PULL_PERCENT = 25;
  const DURATION = 0.8;

  const EASE_NAME =
    'wambolt-interactive-collage-ease';

  const EASE_CURVE =
    '0.3, 0.075, 0, 1';

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const finePointerQuery =
    window.matchMedia(
      FINE_POINTER_QUERY
    );

  const reducedMotionQuery =
    window.matchMedia(
      REDUCED_MOTION_QUERY
    );

  let easeRegistered = false;
  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function hasDependencies() {
    return Boolean(
      window.gsap &&
      window.CustomEase
    );
  }

  function registerDependencies() {
    if (!hasDependencies()) {
      return false;
    }

    if (!easeRegistered) {
      gsap.registerPlugin(CustomEase);

      CustomEase.create(
        EASE_NAME,
        EASE_CURVE
      );

      easeRegistered = true;
    }

    return true;
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getRoots(root = document) {
    const roots = [];

    if (
      root.matches &&
      root.matches(ROOT_SELECTOR)
    ) {
      roots.push(root);
    }

    if (root.querySelectorAll) {
      roots.push(
        ...root.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return roots;
  }


  /* =========================================
     INSTANCE
     ========================================= */

  function initCollage(root) {
    if (
      !root ||
      typeof root
        ._wamboltInteractiveCollageCleanup ===
        'function'
    ) {
      return;
    }

    const list =
      root.querySelector(LIST_SELECTOR);

    const items = Array.from(
      root.querySelectorAll(
        ITEM_SELECTOR
      )
    );

    if (
      !list ||
      !items.length
    ) {
      return;
    }

    const innerByItem = new Map();
    const controlByItem = new Map();

    for (const item of items) {
      const inner =
        item.querySelector(
          INNER_SELECTOR
        );

      const control =
        item.querySelector(
          CONTROL_SELECTOR
        );

      if (!inner || !control) {
        return;
      }

      innerByItem.set(
        item,
        inner
      );

      controlByItem.set(
        item,
        control
      );
    }

    const inners =
      Array.from(
        innerByItem.values()
      );

    const controller =
      new AbortController();

    const { signal } = controller;

    const useTouchInteraction =
      !finePointerQuery.matches;

    let activeItem = null;
    let destroyed = false;


    /* =========================================
       ITEM HELPERS
       ========================================= */

    function getInner(item) {
      return innerByItem.get(item) || null;
    }

    function getControl(item) {
      return controlByItem.get(item) || null;
    }

    function getMoveStrength(distance) {
      const strength =
        1 /
        (
          1 +
          Math.pow(
            distance - 1,
            1.2
          ) *
            0.45
        );

      return distance === 2
        ? strength * SECOND_CARD_BOOST
        : strength;
    }

    function setItemStates(active) {
      items.forEach((item) => {
        const isActive =
          item === active;

        item.toggleAttribute(
          'data-interactive-collage-focus',
          isActive
        );

        const control =
          getControl(item);

        if (
          control &&
          control.tagName === 'BUTTON'
        ) {
          control.setAttribute(
            'aria-pressed',
            isActive
              ? 'true'
              : 'false'
          );
        }
      });
    }


    /* =========================================
       MOTION
       ========================================= */

    function animateItem(
      item,
      xPercent,
      yPercent,
      scale
    ) {
      const inner = getInner(item);

      if (!inner) {
        return;
      }

      if (reducedMotionQuery.matches) {
        gsap.set(inner, {
          xPercent,
          yPercent,
          scale,
        });

        return;
      }

      gsap.to(inner, {
        xPercent,
        yPercent,
        scale,
        duration: DURATION,
        ease: EASE_NAME,
        overwrite: true,
      });
    }

    function resetCollage() {
      if (destroyed) {
        return;
      }

      activeItem = null;
      setItemStates(null);

      items.forEach((item) => {
        animateItem(
          item,
          0,
          0,
          1
        );
      });
    }


    /* =========================================
       FOCUS ITEM
       ========================================= */

    function focusItem(active) {
      if (
        destroyed ||
        !active ||
        activeItem === active
      ) {
        return;
      }

      activeItem = active;
      setItemStates(active);

      const listRect =
        list.getBoundingClientRect();

      const listCenterY =
        listRect.top +
        listRect.height / 2;

      const gap =
        (
          listRect.width *
          GAP_PERCENT
        ) /
        100;

      const orderedItems =
        [...items].sort((a, b) => {
          const aRect =
            a.getBoundingClientRect();

          const bRect =
            b.getBoundingClientRect();

          return (
            aRect.left +
            aRect.width / 2 -
            (
              bRect.left +
              bRect.width / 2
            )
          );
        });

      const activeIndex =
        orderedItems.indexOf(active);

      if (activeIndex < 0) {
        return;
      }

      const activeRect =
        active.getBoundingClientRect();

      const activeCenterX =
        activeRect.left +
        activeRect.width / 2;

      const activeLeft =
        activeCenterX -
        (
          activeRect.width *
          ACTIVE_SCALE
        ) /
          2;

      const activeRight =
        activeCenterX +
        (
          activeRect.width *
          ACTIVE_SCALE
        ) /
          2;

      const leftItem =
        orderedItems[
          activeIndex - 1
        ];

      const rightItem =
        orderedItems[
          activeIndex + 1
        ];

      let leftMove = 0;
      let rightMove = 0;

      if (leftItem) {
        const rect =
          leftItem.getBoundingClientRect();

        const itemRight =
          rect.left +
          rect.width / 2 +
          (
            rect.width *
            INACTIVE_SCALE
          ) /
            2;

        leftMove = Math.min(
          0,
          activeLeft -
            gap -
            itemRight
        );
      }

      if (rightItem) {
        const rect =
          rightItem.getBoundingClientRect();

        const itemLeft =
          rect.left +
          rect.width / 2 -
          (
            rect.width *
            INACTIVE_SCALE
          ) /
            2;

        rightMove = Math.max(
          0,
          activeRight +
            gap -
            itemLeft
        );
      }

      orderedItems.forEach(
        (item, index) => {
          if (item === active) {
            animateItem(
              item,
              0,
              0,
              ACTIVE_SCALE
            );

            return;
          }

          const rect =
            item.getBoundingClientRect();

          const difference =
            index - activeIndex;

          const distance =
            Math.abs(difference);

          const strength =
            getMoveStrength(distance);

          const itemCenterY =
            rect.top +
            rect.height / 2;

          const centerProgress =
            (
              listCenterY -
              itemCenterY
            ) /
            (
              listRect.height /
              2
            );

          const moveX =
            difference < 0
              ? leftMove * strength
              : rightMove * strength;

          const scale =
            INACTIVE_SCALE -
            (
              1 -
              strength
            ) *
              0.12;

          animateItem(
            item,
            (
              moveX /
              rect.width
            ) *
              100,
            CENTER_PULL_PERCENT *
              centerProgress *
              strength,
            scale
          );
        }
      );
    }


    /* =========================================
       POINTER HIT TESTING
       ========================================= */

    function getHoveredItem(event) {
      if (activeItem) {
        const activeInner =
          getInner(activeItem);

        if (activeInner) {
          const rect =
            activeInner
              .getBoundingClientRect();

          if (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
          ) {
            return activeItem;
          }
        }
      }

      const candidate =
        document
          .elementFromPoint(
            event.clientX,
            event.clientY
          )
          ?.closest(
            ITEM_SELECTOR
          );

      if (
        !candidate ||
        !root.contains(candidate)
      ) {
        return null;
      }

      return candidate;
    }


    /* =========================================
       SEMANTIC CONTROLS
       ========================================= */

    items.forEach((item) => {
      const control =
        getControl(item);

      if (
        !control ||
        control.tagName !== 'BUTTON'
      ) {
        return;
      }

      control.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();

          if (activeItem === item) {
            resetCollage();
          } else {
            focusItem(item);
          }
        },
        {
          signal,
        }
      );
    });


    /* =========================================
       POINTER INTERACTION
       ========================================= */

    if (useTouchInteraction) {
      document.addEventListener(
        'click',
        (event) => {
          if (
            !root.contains(
              event.target
            )
          ) {
            resetCollage();
          }
        },
        {
          signal,
        }
      );
    } else {
      root.addEventListener(
        'pointermove',
        (event) => {
          const item =
            getHoveredItem(event);

          if (item) {
            focusItem(item);
          } else {
            resetCollage();
          }
        },
        {
          signal,
        }
      );

      root.addEventListener(
        'pointerleave',
        resetCollage,
        {
          signal,
        }
      );
    }


    /* =========================================
       KEYBOARD
       ========================================= */

    root.addEventListener(
      'focusin',
      (event) => {
        const item =
          event.target.closest?.(
            ITEM_SELECTOR
          );

        if (
          item &&
          root.contains(item)
        ) {
          focusItem(item);
        }
      },
      {
        signal,
      }
    );

    root.addEventListener(
      'focusout',
      (event) => {
        const nextTarget =
          event.relatedTarget;

        if (
          !nextTarget ||
          !root.contains(nextTarget)
        ) {
          resetCollage();
        }
      },
      {
        signal,
      }
    );

    root.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key !== 'Escape' ||
          !activeItem
        ) {
          return;
        }

        event.preventDefault();
        resetCollage();
      },
      {
        signal,
      }
    );


    /* =========================================
       CLEANUP
       ========================================= */

    function cleanupCollage() {
      if (destroyed) {
        return;
      }

      destroyed = true;

      controller.abort();

      gsap.killTweensOf(inners);

      items.forEach((item) => {
        item.removeAttribute(
          'data-interactive-collage-focus'
        );

        const control =
          getControl(item);

        if (
          control &&
          control.tagName === 'BUTTON'
        ) {
          control.setAttribute(
            'aria-pressed',
            'false'
          );
        }
      });

      gsap.set(inners, {
        clearProps:
          'transform',
      });

      activeItem = null;

      delete root
        ._wamboltInteractiveCollageCleanup;

      delete root
        ._interactiveCollageAbort;
    }

    /*
     * The AbortController property is retained
     * temporarily for compatibility with the current
     * global Barba cleanup layer.
     */
    root._interactiveCollageAbort =
      controller;

    root._wamboltInteractiveCollageCleanup =
      cleanupCollage;
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initAll(root = document) {
    const roots = getRoots(root);

    if (!roots.length) {
      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    roots.forEach(initCollage);

    return true;
  }

  function destroyAll(root = document) {
    getRoots(root).forEach(
      (collageRoot) => {
        if (
          typeof collageRoot
            ._wamboltInteractiveCollageCleanup ===
            'function'
        ) {
          collageRoot
            ._wamboltInteractiveCollageCleanup();
        }
      }
    );
  }

  function rebuildAll(root = document) {
    destroyAll(root);

    if (!initAll(root)) {
      scheduleDependencyRetry();
    }
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      window.clearTimeout(
        dependencyRetryTimer
      );

      dependencyRetryTimer = null;
    }
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Interactive Collage could not initialize because GSAP or CustomEase is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          if (initAll(document)) {
            dependencyRetryAttempts = 0;
            clearDependencyRetry();

            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (initAll(document)) {
      dependencyRetryAttempts = 0;
      clearDependencyRetry();

      return;
    }

    scheduleDependencyRetry();
  }


  /* =========================================
     INTERACTION MODE CHANGES
     ========================================= */

  function handleInteractionModeChange() {
    rebuildAll(document);
  }

  function addMediaQueryListener(
    query,
    handler
  ) {
    if (
      typeof query.addEventListener ===
      'function'
    ) {
      query.addEventListener(
        'change',
        handler
      );

      return;
    }

    if (
      typeof query.addListener ===
      'function'
    ) {
      query.addListener(handler);
    }
  }

  addMediaQueryListener(
    finePointerQuery,
    handleInteractionModeChange
  );

  addMediaQueryListener(
    reducedMotionQuery,
    handleInteractionModeChange
  );


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (!initAll(event.target)) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyAll(event.target);
    }
  );
})();